import { call, put, select, takeEvery } from 'redux-saga/effects';
import { AnyAction } from 'redux';
import {
  getSelectedZusatzoptionen,
  GlobalActions,
  GlobalPut,
  ThhPferdProduktPayload,
} from '@ps20-frontend/shop-integration-redux';
import { App, Produktlinie, THH_PFERD_UEBERSICHT_TITLE } from '@ps20-frontend/frontend-models';
import { getSelectedProduktlinie } from '@ps20-frontend/preis-lib';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { ThhTier, Tiergroesse } from '@ps20/bff-models';
import { IAppState } from '../appState';
import { ThhPferdActions } from '../thhPferdActions';
import { AnzahlTiereEnum, Versicherungsnehmer } from '../reducers/versicherungsnehmer.reducer';
import contentData from '../../data/content-default.json';
import { convertStockmassToTiergroesse } from './transformer/thhPferdData.transformer';

export function* watchInDashboardUebernehmenSaga() {
  yield takeEvery(
    ThhPferdActions.ABSPRUNG_INS_DASHBOARD,
    safeSaga(generateDashboardData, App.THH_PFERD),
  );
  yield takeEvery(
    ThhPferdActions.ABSPRUNG_INS_DASHBOARD,
    safeSaga(generateProduktData, App.THH_PFERD),
  );
}

export function* generateProduktData() {
  const globalPut = GlobalPut.getInstance();
  const state: IAppState = yield select();
  const hasOnlyOneHorse: boolean = state.versicherungsnehmer.anzahlTiere === AnzahlTiereEnum.EINES;

  const produktActionData: ThhPferdProduktPayload = {
    appId: App.THH_PFERD,
    laufzeit: state.vertragsoptionen.vertragslaufzeit,
    uebersichtTitle: THH_PFERD_UEBERSICHT_TITLE,
    versichertesRisiko: getVersichertesRisiko(
      state.versicherungsnehmer.anzahlTiere,
      state.versicherungsnehmer.anzahlMehrerePferde,
    ),
    vorvertragsnummer: state.vertragsoptionen.vorvertragsNummern,
    anzahlPferde: getAnzahlPferde(state.versicherungsnehmer),
    tiergroesseList: getTiergroesseList(hasOnlyOneHorse, state),
    vorvertragSofortschutz: state.vertragsoptionen.vorvertragSofortschutz,
    beginndatum: state.vertragsoptionen.beginnDatum,
    tiere:
      state.versicherungsnehmer.tiere?.length > 0
        ? getTiere(state.versicherungsnehmer.tiere, getTiergroesseList(hasOnlyOneHorse, state))
        : [],
    isWeitererVertrag: state.vertragsoptionen.isWeitererVertrag,
    angebotsdatum: state.vertragsoptionen.angebotsdatum,
    ...(state.versicherungsnehmer.tiergruppeAnimals
      ? { tiergruppeAnimals: state.versicherungsnehmer.tiergruppeAnimals }
      : {}),
  };

  yield call(globalPut.put, new GlobalActions().produktAction(produktActionData));
}

function getTiere(tiere: ThhTier[], tiergroesseList: Tiergroesse[]): ThhTier[] {
  const newList: ThhTier[] = [];
  const tierTicker = tiere.map(s => s);
  tiergroesseList.forEach(value => {
    const findElement = tierTicker.find(value1 => value1.tiergroesse === value);
    if (findElement && tierTicker.includes(findElement)) {
      tierTicker.splice(tierTicker.indexOf(findElement), 1);
      newList.push({
        instanzId: findElement.instanzId,
        tierrasse: findElement.tierrasse,
        tiergroesse: findElement.tiergroesse,
        geburtsdatumTier: findElement.geburtsdatumTier,
        geschlecht: findElement.geschlecht,
        identifizierungsart: findElement.identifizierungsart,
        identifizierungscode: findElement.identifizierungscode,
        identifizierungsstelle: findElement.identifizierungsstelle,
        buchnummer: findElement.buchnummer,
        tierart: findElement.tierart,
        tiername: findElement.tiername,
      });
    } else {
      newList.push({
        geburtsdatumTier: '',
        geschlecht: '',
        identifizierungsart: '',
        identifizierungscode: '',
        identifizierungsstelle: '',
        instanzId: 0,
        tierart: undefined,
        buchnummer: '',
        tiername: '',
        tierrasse: '',
        tiergroesse: value,
      });
    }
  });
  return newList;
}
function getTiergroesseList(hasOnlyOneHorse: boolean, state: IAppState): Tiergroesse[] {
  return hasOnlyOneHorse
    ? [convertStockmassToTiergroesse(state.versicherungsnehmer.stockmass)]
    : state.versicherungsnehmer.stockmassMehrerePferde.map(value =>
        convertStockmassToTiergroesse(value),
      );
}

function getAnzahlPferde(versicherungsnehmer: Versicherungsnehmer): number {
  const anzahlTiereEnumToAnzahlMapper = {
    [AnzahlTiereEnum.EINES]: 1,
    [AnzahlTiereEnum.MEHRERE]: versicherungsnehmer.anzahlMehrerePferde,
  };
  return anzahlTiereEnumToAnzahlMapper[versicherungsnehmer.anzahlTiere];
}

export function* generateDashboardData(action?: AnyAction) {
  const state: IAppState = yield select();
  const globalPut = GlobalPut.getInstance();
  const selectedProduktlinie: Produktlinie = getSelectedProduktlinie(state.produkt);

  yield put(new ThhPferdActions().vorschlagdatenChanged(action?.payload?.deferTarifierung === true));

  yield call(
    globalPut.put.bind(globalPut),
    new GlobalActions().inDashboardUebernehmen({
      appId: App.THH_PFERD,
      kurztext: selectedProduktlinie.kurztext,
      ausgewaehlteLinie: selectedProduktlinie.produktName,
      versichertesRisiko: getVersichertesRisiko(
        state.versicherungsnehmer.anzahlTiere,
        state.versicherungsnehmer.anzahlMehrerePferde,
      ),
      vertragslaufzeit: state.vertragsoptionen.vertragslaufzeit,
      selectZusatzoptionen: getSelectedZusatzoptionen(selectedProduktlinie.zusatzoptionen),
      beginn: state.vertragsoptionen.beginnDatum,
      ...(action?.payload?.deferTarifierung ? { deferTarifierung: true } : {}),
    }),
  );
}

function getVersichertesRisiko(mehrereTiere: AnzahlTiereEnum, anzahl: number): string {
  if (mehrereTiere === AnzahlTiereEnum.EINES) {
    return contentData['versichertesRisiko']['einPferd'];
  } else if (mehrereTiere === AnzahlTiereEnum.MEHRERE) {
    return contentData['versichertesRisiko']['mehrerePferde'] + ` (${anzahl})`;
  }
  return '';
}
